/**
 * Storefront v6 — the tenant data layer.
 *
 * ONE hook that reads everything the storefront shows through the existing
 * PUBLIC, tenant-scoped endpoints (nothing new is invented here):
 *   /api/meal-pass/public/{sub}/programs      passes + branding + tenant meta
 *   /api/storefront/meal-plans/{sub}          plans (+ store_info)
 *   /api/storefront/tags/{sub}                plan tags
 *   /api/storefront/meal-items/{sub}          display feed / for_order feed
 *   /api/storefront/settings/{sub}            hours, open state, currency,
 *                                             tax, minimum order, pickup address
 *   /api/storefront/menu-calendar/{sub}       date-mode menus
 *   /api/catering/public/{sub}/packages       catering + availability
 * Every fetch carries the store slug when the URL names a store (v5.65.2:
 * a store's page lists only its own catalogue).
 *
 * In the Design Studio preview (?preview=1) an empty catalogue is replaced
 * by the demo fixtures and the live config arrives by postMessage — the
 * same protocol the editor already speaks.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCustomDomainMapping } from '../../../../utils/customDomain';
import {
  DEMO_PROGRAMS, DEMO_MEAL_PLANS, DEMO_TAGS, DEMO_MEAL_ITEMS, DEMO_MENU_CALENDAR,
  DEMO_CATERING_PACKAGES, DEMO_CATERING_AVAILABILITY,
} from '../../mealpass/storefrontPreviewFixtures';

/** The tenant for this page: ?tenant= on localhost, the subdomain on prod, the session as a last resort. */
export const resolveSubdomain = () => {
  const custom = getCustomDomainMapping();
  if (custom) return custom.subdomain;
  const urlParams = new URLSearchParams(window.location.search);
  const explicit = urlParams.get('tenant') || urlParams.get('subdomain');
  if (explicit) return explicit;
  const hostname = window.location.hostname;
  if (hostname.includes('.plateos.io')) return hostname.replace('.plateos.io', '');
  try {
    const s = JSON.parse(sessionStorage.getItem('customerSession') || '{}');
    if (s?.tenant_subdomain) return s.tenant_subdomain;
  } catch (_e) { /* ignore */ }
  return 'staging';
};

export const getCustomerSessionToken = () => {
  try {
    const s = JSON.parse(sessionStorage.getItem('customerSession') || '{}');
    return s?.token || null;
  } catch (_e) { return null; }
};

export const isPreviewMode = () => new URLSearchParams(window.location.search).get('preview') === '1';

const EMPTY = Object.freeze([]);

export default function useStorefrontData({ storeSlug = null, strictCatalogue = false, enabled = true } = {}) {
  const isPreview = useMemo(isPreviewMode, []);
  const allowDemo = isPreview && !strictCatalogue && !new URLSearchParams(window.location.search).has('website_draft');
  const subdomain = useMemo(resolveSubdomain, []);
  const isAuthed = Boolean(getCustomerSessionToken());

  const activeRequest = useRef(null);
  const activeScope = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brand, setBrand] = useState(null);
  const [tenantMeta, setTenantMeta] = useState(null);
  const [programs, setPrograms] = useState(EMPTY);
  const [plans, setPlans] = useState(EMPTY);
  const [tags, setTags] = useState(EMPTY);
  const [items, setItems] = useState(EMPTY);
  const [orderable, setOrderable] = useState({ ids: new Set(), reasons: {}, count: 0, sellableCount: 0 });
  const [settings, setSettings] = useState(null);
  const [menuCalendar, setMenuCalendar] = useState(EMPTY);
  const [catering, setCatering] = useState({ packages: EMPTY, availability: null, dishAddons: {}, dishImages: {} });
  const [demoDataActive, setDemoDataActive] = useState(false);

  const withStoreSlug = useCallback((url) => {
    if (!storeSlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}store_slug=${encodeURIComponent(storeSlug)}`;
  }, [storeSlug]);

  const fetchAll = useCallback(async () => {
    if (!enabled) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const feedChannel = isAuthed ? 'customer_dashboard' : 'storefront';
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
    setLoading(true);
    setError(null);
    const scope = JSON.stringify([subdomain, storeSlug, feedChannel, allowDemo]);
    if (activeScope.current !== scope) {
      activeScope.current = scope;
      setPrograms(EMPTY); setPlans(EMPTY); setItems(EMPTY);
      setTags(EMPTY); setMenuCalendar(EMPTY); setSettings(null);
      setCatering({ packages: EMPTY, availability: null, dishAddons: {}, dishImages: {} });
      setOrderable({ ids: new Set(), reasons: {}, count: 0, sellableCount: 0 });
    }
    let demo = false;
    try {
      // Launch and decode independent feeds together, then publish one coherent
      // catalogue. Plans must not wait for branding, tags or the menu calendar.
      const responses = await Promise.allSettled([
        withStoreSlug(`${base}/api/meal-pass/public/${subdomain}/programs`),
        withStoreSlug(`${base}/api/storefront/meal-plans/${subdomain}?channel=${feedChannel}`),
        withStoreSlug(`${base}/api/storefront/tags/${subdomain}`),
        withStoreSlug(`${base}/api/storefront/meal-items/${subdomain}?channel=${feedChannel}`),
        withStoreSlug(`${base}/api/storefront/meal-items/${subdomain}?for_order=1`),
        withStoreSlug(`${base}/api/storefront/settings/${subdomain}`),
        withStoreSlug(`${base}/api/storefront/menu-calendar/${subdomain}`),
        withStoreSlug(`${base}/api/catering/public/${subdomain}/packages`),
      ].map(async (url) => {
        const response = await fetch(url, { signal: controller.signal });
        const json = response.ok ? await response.json() : null;
        return { ok: response.ok, status: response.status, data: json };
      }));
      if (activeRequest.current !== controller) return;
      if (timedOut) throw new Error('The storefront is taking too long to respond. Please try again.');
      if (controller.signal.aborted) return;
      const responseAt = (index) => {
        const response = responses[index];
        if (response.status === 'rejected') throw response.reason;
        return response.value;
      };
      // An unavailable catalogue is not an empty catalogue. Optional modules
      // can be disabled (403/404), but transport/server errors need a retry.
      if (!allowDemo) {
        for (const index of [0, 1, 3, 4, 5, 7]) {
          const response = responseAt(index);
          if (!response.ok && (index === 0 || ![403, 404].includes(response.status))) {
            throw new Error('Could not load this storefront. Please try again.');
          }
        }
      }
      // Replace optional feeds as well: a module disabled since the previous
      // refresh must clear its old rows. All following updates are synchronous.
      setPrograms(EMPTY); setPlans(EMPTY); setItems(EMPTY);
      setTags(EMPTY); setMenuCalendar(EMPTY); setSettings(null);
      setCatering({ packages: EMPTY, availability: null, dishAddons: {}, dishImages: {} });
      setOrderable({ ids: new Set(), reasons: {}, count: 0, sellableCount: 0 });
      // 1. passes + branding + tenant meta (the one call that must succeed)
      const res = responseAt(0);
      if (!res.ok && !allowDemo) {
        setError(`Could not load this storefront (${res.status})`);
        setPrograms(EMPTY);
        setLoading(false);
        return;
      }
      if (!res.ok) {
        setPrograms(DEMO_PROGRAMS);
        demo = true;
      } else {
        const data = res.data;
        const fetched = data.programs || [];
        if (allowDemo && !fetched.length) { setPrograms(DEMO_PROGRAMS); demo = true; } else setPrograms(fetched);
        setBrand(data.branding || {});
        setTenantMeta(data.tenant_meta || null);
      }
      // 2. plans
      let usedDemoPlans = false;
      try {
        const r = responseAt(1);
        if (r.ok) {
          const j = r.data;
          const list = (j.meal_plans || []).filter((p) => p.id !== 'system-custom-plan' && p.is_active !== false);
          if (allowDemo && !list.length) { setPlans(DEMO_MEAL_PLANS); usedDemoPlans = true; demo = true; } else setPlans(list);
        } else if (allowDemo) { setPlans(DEMO_MEAL_PLANS); usedDemoPlans = true; demo = true; }
      } catch (_e) { /* optional */ }
      // 3. tags
      try {
        const r = responseAt(2);
        if (r.ok) { const j = r.data; setTags(usedDemoPlans ? DEMO_TAGS : (j.tags || [])); }
        else if (allowDemo && usedDemoPlans) setTags(DEMO_TAGS);
      } catch (_e) { /* optional */ }
      // 4. the display feed (what the menu shows)
      try {
        const r = responseAt(3);
        if (r.ok) {
          const j = r.data;
          const list = (j.meal_items || []).filter((m) => m.is_active !== false);
          if (allowDemo && !list.length) { setItems(DEMO_MEAL_ITEMS); demo = true; } else setItems(list);
        } else if (allowDemo) { setItems(DEMO_MEAL_ITEMS); demo = true; }
      } catch (_e) { /* optional */ }
      // 5. the for_order feed (what can be added — the order page's own rule)
      try {
        const r = responseAt(4);
        if (r.ok) {
          const j = r.data;
          const list = j.meal_items || [];
          setOrderable({
            count: list.length,
            // v5.67.2 - `count` is every row the feed returns, INCLUDING the 86'd ones it
            // refuses; the storefront no longer lists those, so offering "Order now" off
            // `count` would point at a menu this page has hidden. Off-window dishes stay
            // counted: they are still listed, and /order can still schedule them.
            sellableCount: list.filter((it) => !it.out_of_stock).length,
            ids: new Set(list.filter((it) => it.orderable !== false && !it.out_of_stock).map((it) => it.id)),
            reasons: Object.fromEntries(list.filter((it) => it.unavailable_reason).map((it) => [it.id, it.unavailable_reason])),
          });
        }
      } catch (_e) { /* optional */ }
      // 6. settings: hours, open state, currency, tax, minimum order, pickup address
      try {
        const r = responseAt(5);
        if (r.ok) setSettings(r.data);
      } catch (_e) { /* optional */ }
      // 7. date-mode menu
      try {
        const r = responseAt(6);
        if (r.ok) {
          const j = r.data;
          const entries = j.entries || j.menu || [];
          if (allowDemo && !entries.length) setMenuCalendar(DEMO_MENU_CALENDAR); else setMenuCalendar(entries);
        }
      } catch (_e) { /* optional */ }
      // 8. catering
      try {
        const r = responseAt(7);
        if (r.ok) {
          const j = r.data;
          const pk = j.packages || [];
          if (allowDemo && !pk.length) {
            setCatering({ packages: DEMO_CATERING_PACKAGES, availability: DEMO_CATERING_AVAILABILITY, dishAddons: {}, dishImages: {} });
            demo = true;
          } else {
            setCatering({ packages: pk, availability: j.availability || null, dishAddons: j.dish_addons || {}, dishImages: j.dish_images || {} });
          }
        } else if (allowDemo) {
          setCatering({ packages: DEMO_CATERING_PACKAGES, availability: DEMO_CATERING_AVAILABILITY, dishAddons: {}, dishImages: {} });
          demo = true;
        }
      } catch (_e) { /* optional */ }
    } catch (e) {
      if (activeRequest.current === controller && (!controller.signal.aborted || timedOut) && !allowDemo) {
        setError(timedOut ? 'The storefront is taking too long to respond. Please try again.' : 'Could not load this storefront. Please try again.');
      }
    } finally {
      clearTimeout(timeout);
      if (activeRequest.current === controller && (!controller.signal.aborted || timedOut)) {
        setDemoDataActive(demo);
        setLoading(false);
      }
    }
  }, [subdomain, storeSlug, isAuthed, allowDemo, withStoreSlug, enabled]);

  useEffect(() => {
    fetchAll();
    return () => { activeRequest.current?.abort(); activeRequest.current = null; };
  }, [fetchAll]);

  // Design Studio live preview: the editor posts the config as the merchant types.
  useEffect(() => {
    if (!isPreview) return undefined;
    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'MEAL_PASS_PREVIEW_CONFIG') return;
      if (data.branding && typeof data.branding === 'object') {
        setBrand(data.branding);
        setTenantMeta((prev) => prev || { name: data.branding.brand_name, subdomain });
        // Editing branding does not mean the catalogue has finished loading.
      }
    };
    window.addEventListener('message', onMessage);
    try { window.parent?.postMessage({ type: 'MEAL_PASS_PREVIEW_READY' }, '*'); } catch (_e) { /* noop */ }
    return () => window.removeEventListener('message', onMessage);
  }, [isPreview, subdomain]);

  return {
    subdomain, isPreview, isAuthed, loading, error, refetch: fetchAll,
    brand: brand || {}, tenantMeta, programs, plans, tags, items, orderable, settings, menuCalendar,
    catering, demoDataActive,
  };
}
